import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../shared/navbar';
import { API_BASE_URL } from '../../config';
import StripeAutopaySetup from '../financial/StripeAutopaySetup';
import PaymentProcessingModal from '../financial/PaymentProcessingModal';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import '../financial/PortfolioDashboard.css';
import './TenantDetailView.css';


const TenantDetailView = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAutopayModal, setShowAutopayModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [generatingPayments, setGeneratingPayments] = useState(false);
  const [success, setSuccess] = useState('');
  const [tenantScore, setTenantScore] = useState(null);
  const [scoreExpanded, setScoreExpanded] = useState(false);

  const stripePromise = loadStripe('pk_test_51Px9X7GvXm129m7qC7z9z9z9z9z9z9z9z'); 

  useEffect(() => {
    fetchTenantData();
    fetchDocuments();
    fetchTenantScore();
  }, [tenantId]);

  const fetchTenantScore = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenant-scores/${tenantId}`);
      if (res.ok) setTenantScore(await res.json());
    } catch (e) {
      console.error('Error fetching tenant score:', e);
    }
  };

  const handleGeneratePayments = async () => {
    try {
      setGeneratingPayments(true);
      const response = await fetch(`${API_BASE_URL}/api/tenant/${tenantId}/generate-payments`, {
        method: 'POST'
      });
      if (response.ok) {
        setSuccess('Payments generated successfully!');
        fetchTenantData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to generate payments');
      }
    } catch (err) {
      console.error('Error generating payments:', err);
      alert('Error generating payments');
    } finally {
      setGeneratingPayments(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/documents`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const fetchTenantData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/portfolio/tenant/${tenantId}`);
      if (response.ok) {
        const data = await response.json();
        setTenantData(data);
      } else {
        setError('Failed to load tenant data');
      }
    } catch (err) {
      console.error('Error fetching tenant:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'good_standing': return '#10b981';
      case 'overdue': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="tenant-detail-view">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading tenant data...</p>
        </div>
      </div>
    );
  }

  if (error || !tenantData) {
    return (
      <div className="tenant-detail-view">
        <div className="error-banner">{error || 'Tenant not found'}</div>
        <button onClick={() => navigate('/financial')} className="btn-secondary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  const tenant = tenantData.tenant;
  const balance = tenantData.balance;

  return (
    <div className="tenant-detail-view">
      <Navbar selected="Financial" />
      <div className="detail-container">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <button onClick={() => navigate('/financial')} className="back-button">
            ← Back to Portfolio
          </button>
          <h1>{tenant.first_name || tenant['First Name']} {tenant.last_name || tenant['Last Name']}</h1>
          <p className="property-address">{tenant.email || tenant['Email']} • {tenant.phone || tenant['Phone']}</p>
          <div className="property-stats-row">
            <div className="stat-item">
              <span className="stat-label">Unit:</span>
              <span className="stat-value">{tenant.unit_number || tenant['Unit Address'] || '-'}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Property:</span>
              <span 
                className="stat-value property-link" 
                onClick={() => navigate(`/portfolio/property/${tenant.property_id}`)}
              >
                {tenant.property_name || '-'}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Status:</span>
              <span className="stat-value status-value" style={{ color: getStatusColor(balance.status) }}>
                {balance.status === 'good_standing' ? 'In Good Standing' : `${balance.days_overdue} Days Overdue`}
              </span>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="action-btn autopay-btn" 
            onClick={() => setShowAutopayModal(true)}
          >
            🔒 Stripe Autopay
          </button>
          <button 
            className="action-btn sync-btn" 
            onClick={handleGeneratePayments}
            disabled={generatingPayments}
          >
            {generatingPayments ? 'Generating...' : '🔄 Sync Payments'}
          </button>
        </div>
      </div>

      {success && (
        <div className="success-banner" style={{ 
          backgroundColor: '#d1fae5', 
          color: '#065f46', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontWeight: 600,
          textAlign: 'center'
        }}>
          {success}
        </div>
      )}

      {/* Renewal Score Panel */}
      {tenantScore && (() => {
        const SCORE_COLORS = { green: '#16a34a', yellow: '#ca8a04', orange: '#ea580c', red: '#dc2626' };
        const SCORE_BG    = { green: '#f0fdf4', yellow: '#fefce8', orange: '#fff7ed', red: '#fef2f2' };
        const c  = SCORE_COLORS[tenantScore.recommendation_class] || '#6b7280';
        const bg = SCORE_BG[tenantScore.recommendation_class]    || '#f9fafb';
        return (
          <div className="dashboard-section" style={{ background: bg, border: `1px solid ${c}25`, borderRadius: 12, padding: '18px 20px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              {/* Score + recommendation */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', border: `4px solid ${c}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, color: c, flexShrink: 0 }}>
                  {tenantScore.score}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>Renewal Score</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: c }}>{tenantScore.recommendation}</div>
                  <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
                    {[
                      [`${tenantScore.default_count} default${tenantScore.default_count !== 1 ? 's' : ''}`, tenantScore.default_count > 0],
                      [`${tenantScore.late_count} late`, tenantScore.late_count > 0],
                      [`${tenantScore.ontime_count} on-time`, false],
                      [`${tenantScore.maintenance_count} maintenance`, false],
                      [`${tenantScore.complaint_count} complaint${tenantScore.complaint_count !== 1 ? 's' : ''}`, tenantScore.complaint_count > 0],
                    ].map(([label, isBad]) => (
                      <span key={label} style={{ fontSize: 12, color: isBad ? '#dc2626' : '#64748b', fontWeight: isBad ? 600 : 400 }}>{label}</span>
                    ))}
                  </div>
                </div>
              </div>
              {/* Toggle breakdown */}
              <button
                onClick={() => setScoreExpanded(x => !x)}
                style={{ padding: '7px 16px', background: '#fff', border: `1px solid ${c}50`, borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: c }}
              >
                {scoreExpanded ? 'Hide Breakdown' : 'View Breakdown'}
              </button>
            </div>

            {/* Score breakdown (expanded) */}
            {scoreExpanded && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${c}25` }}>
                {tenantScore.factors.length === 0 ? (
                  <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>No deductions — perfect record.</p>
                ) : tenantScore.factors.map((f, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid rgba(0,0,0,.05)', fontSize: 13 }}>
                    <span style={{ color: '#374151' }}>{f.factor}</span>
                    <span style={{ fontWeight: 700, color: f.impact > 0 ? '#16a34a' : '#dc2626' }}>
                      {f.impact > 0 ? '+' : ''}{f.impact} pts
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Balance Summary */}
      <div className="dashboard-section">
        <h2>Balance Summary</h2>
        <div className="balance-grid">
          <div className="balance-card">
            <div className="balance-label">Current Balance</div>
            <div className="balance-value" style={{ 
              color: balance.current_balance > 0 ? '#ef4444' : balance.current_balance < 0 ? '#10b981' : '#6b7280',
              fontSize: '32px',
              fontWeight: 700
            }}>
              {formatCurrency(balance.current_balance)}
            </div>
            <div className="balance-note">
              {balance.current_balance > 0 ? 'Amount owed' : balance.current_balance < 0 ? 'Credit balance' : 'Paid in full'}
            </div>
          </div>
          <div className="balance-card">
            <div className="balance-label">Total Paid</div>
            <div className="balance-value" style={{ fontSize: '24px', fontWeight: 600, color: '#10b981' }}>
              {formatCurrency(balance.paid_total)}
            </div>
          </div>
          <div className="balance-card">
            <div className="balance-label">Next Due Date</div>
            <div className="balance-value" style={{ fontSize: '20px', fontWeight: 600 }}>
              {formatDate(balance.next_due_date)}
            </div>
            <div className="balance-note">
              {balance.next_due_amount > 0 ? formatCurrency(balance.next_due_amount) : 'No upcoming payments'}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="dashboard-section">
        <h2>Activity Timeline</h2>
        <div className="timeline-container">
          {tenantData.timeline.map((event, idx) => (
            <div key={idx} className="timeline-item">
              <div className="timeline-marker" style={{
                backgroundColor: event.type === 'payment' ? '#10b981' : '#3b82f6'
              }}></div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <span className="timeline-description">{event.description}</span>
                  <span className="timeline-amount">{formatCurrency(event.amount)}</span>
                </div>
                <div className="timeline-detail">{event.detail}</div>
                <div className="timeline-date">{formatDate(event.created_at)}</div>
                <div className="timeline-status">
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: 600,
                    backgroundColor: event.status === 'paid' ? '#d4edda' : '#fff3cd',
                    color: event.status === 'paid' ? '#155724' : '#856404'
                  }}>
                    {event.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invoices & Payments Table */}
      <div className="dashboard-section">
        <h2>Invoices & Payments</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Invoice #</th>
                <th>Period</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tenantData.records.map(record => (
                <tr key={`${record.record_type}-${record.id}`}>
                  <td>{formatDate(record.due_date)}</td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: 600,
                      backgroundColor: record.record_type === 'payment' ? '#d4edda' : '#d1ecf1',
                      color: record.record_type === 'payment' ? '#155724' : '#0c5460'
                    }}>
                      {record.record_type === 'payment' ? 'Payment' : 'Invoice'}
                    </span>
                  </td>
                  <td>{record.invoice_number || record.reference_number || '-'}</td>
                  <td>
                    {record.period_start && record.period_end 
                      ? `${formatDate(record.period_start)} - ${formatDate(record.period_end)}`
                      : '-'}
                  </td>
                  <td>{formatDate(record.due_date)}</td>
                  <td>{formatCurrency(record.amount)}</td>
                  <td>{formatCurrency(record.paid_amount)}</td>
                  <td>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 600,
                      backgroundColor: record.status === 'paid' ? '#d4edda' : 
                                     record.status === 'overdue' ? '#f8d7da' : '#fff3cd',
                      color: record.status === 'paid' ? '#155724' : 
                            record.status === 'overdue' ? '#721c24' : '#856404'
                    }}>
                      {record.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lease Information */}
      {tenantData.lease && (
        <div className="dashboard-section">
          <h2>Lease Information</h2>
          <div className="lease-info">
            <div className="info-row">
              <span className="info-label">Lease Term:</span>
              <span className="info-value">
                {formatDate(tenantData.lease.start_date)} - {formatDate(tenantData.lease.end_date) || 'Ongoing'}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Monthly Rent:</span>
              <span className="info-value">{formatCurrency(tenantData.lease.rent_amount)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Due Day:</span>
              <span className="info-value">{tenantData.lease.due_day || 1} of each month</span>
            </div>
            <div className="info-row">
              <span className="info-label">Deposit:</span>
              <span className="info-value">{formatCurrency(tenantData.lease.deposit_amount)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Documents Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>Documents</h2>
          <button 
            onClick={() => setShowUploadModal(true)}
            className="export-btn"
          >
            + Upload Document
          </button>
        </div>
        {documents.length === 0 ? (
          <div className="empty-state">No documents available</div>
        ) : (
          <div className="documents-grid">
            {documents.map(doc => (
              <div key={doc.id} className="document-card-small">
                <div className="document-icon-small">
                  {doc.file_type?.includes('pdf') ? '📄' : 
                   doc.file_type?.includes('image') ? '🖼️' : '📎'}
                </div>
                <div className="document-info-small">
                  <div className="document-name-small">{doc.original_filename || doc.filename}</div>
                  <div className="document-meta-small">
                    {doc.category && <span className="document-category">{doc.category}</span>}
                    {doc.document_type && <span className="document-type">{doc.document_type}</span>}
                    {formatDate(doc.upload_date)}
                  </div>
                </div>
                <button
                  onClick={() => window.open(`${API_BASE_URL}/api/documents/${doc.id}/download`, '_blank')}
                  className="download-btn-small"
                >
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Upload Document</h2>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <DocumentUploadForm
                tenantId={tenantId}
                onUploadComplete={() => {
                  setShowUploadModal(false);
                  fetchDocuments();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Stripe Autopay Modal */}
      {showAutopayModal && (
        <StripeAutopaySetup
          tenantId={tenantId}
          tenantName={`${tenant.first_name || tenant['First Name'] || ''} ${tenant.last_name || tenant['Last Name'] || ''}`.trim()}
          tenantEmail={tenant.email || tenant['Email']}
          onSuccess={(data) => {
            setShowAutopayModal(false);
            setSuccess(data.deactivated ? 'Autopay deactivated successfully!' : 'Autopay setup successfully!');
            fetchTenantData();
            setTimeout(() => setSuccess(''), 3000);
          }}
          onClose={() => setShowAutopayModal(false)}
          mode="manager"
        />
      )}
      </div>
    </div>
  );
};

const DocumentUploadForm = ({ tenantId, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState('payment_receipt');
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', description);
      formData.append('document_type', documentType);
      formData.append('category', 'financial');

      const response = await fetch(`${API_BASE_URL}/api/financial/tenants/${tenantId}/documents`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        onUploadComplete();
      } else {
        alert('Error uploading document');
      }
    } catch (err) {
      console.error('Error uploading:', err);
      alert('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="document-upload-form">
      <div className="form-field">
        <label>Document Type</label>
        <select
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
          className="form-input"
        >
          <option value="payment_receipt">Payment Receipt</option>
          <option value="bank_statement">Bank Statement</option>
          <option value="invoice_receipt">Invoice Receipt</option>
        </select>
      </div>
      <div className="form-field">
        <label>File</label>
        <input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          className="form-input"
          required
        />
      </div>
      <div className="form-field">
        <label>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="form-input"
          rows="3"
        />
      </div>
      <div className="modal-actions">
        <button type="submit" className="save-button" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
        <button type="button" className="cancel-button" onClick={() => onUploadComplete()}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default TenantDetailView;

